import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart,
  Users,
  Shield,
  Award,
  Target,
  Globe,
  Star,
  ArrowLeft,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import Navbar from "../Landing/Navbar";
import Footer from "../Components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const stagger = { show: { transition: { staggerChildren: 0.06 } } };

const values = [
  { Icon: Heart,  title: "Traditional values", desc: "We honour and preserve the rich cultural heritage of the Telugu Goud community." },
  { Icon: Shield, title: "Trust & security",    desc: "Your privacy and security are our top priorities. We ensure safe and verified matchmaking." },
  { Icon: Users,  title: "Community focus",     desc: "Building lasting relationships within our community through genuine connections." },
  { Icon: Award,  title: "Excellence",          desc: "Premium matrimonial services with attention to detail and personalised care." },
];

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="page-container max-w-4xl pt-4 pb-app-nav sm:pt-7 lg:pt-10">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-sand-200 bg-white px-3.5 text-[12px] font-semibold text-ink-600 transition hover:border-brand-200 hover:text-brand-700"
        >
          <ArrowLeft size={14} /> Back
        </button>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5 border-b border-sand-100 pb-7 sm:pb-9"
        >
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-700">
              <Sparkles size={12} /> About Karanki Matrimony
            </span>
            <h1 className="mt-3 font-display text-[30px] font-bold leading-tight text-ink-800 sm:text-4xl">
              Connecting hearts.
              <br />
              <span className="text-brand-700">Preserving traditions.</span>
            </h1>
            <p className="mt-3 max-w-2xl text-[13px] leading-6 text-ink-500 sm:text-[14px] sm:leading-7">
              A dedicated matrimonial platform for the Telugu Goud community — blending tradition with technology to help you find the right life partner.
            </p>
            <Link
              to="/register"
              className="mt-5 inline-flex min-h-10 items-center gap-2 rounded-xl bg-brand-700 px-4 text-[12px] font-semibold text-white transition hover:bg-brand-800"
            >
              Start your journey <ChevronRight size={16} />
            </Link>
          </div>
        </motion.section>

        {/* Mission / Vision */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="grid gap-6 border-b border-sand-100 py-7 sm:grid-cols-2 sm:gap-10 sm:py-8"
        >
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 text-brand-700">
              <Target size={17} />
              <h2 className="font-display text-[16px] font-bold text-ink-800">Our mission</h2>
            </div>
            <p className="mt-2.5 text-[13px] leading-6 text-ink-500 sm:text-[14px] sm:leading-7">
              Create a trusted, technology-driven platform that honours traditional values while embracing modern matchmaking.
              Every member should find their perfect life partner through verified profiles and personalised guidance.
            </p>
          </motion.div>
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 text-brand-700">
              <Globe size={17} />
              <h2 className="font-display text-[16px] font-bold text-ink-800">Our vision</h2>
            </div>
            <p className="mt-2.5 text-[13px] leading-6 text-ink-500 sm:text-[14px] sm:leading-7">
              To be the most trusted and preferred matrimonial platform for the Telugu Goud community worldwide — where
              technology blends seamlessly with tradition to create meaningful matches.
            </p>
          </motion.div>
        </motion.section>

        {/* Our story */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="border-b border-sand-100 py-7 sm:py-8"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-700">Our story</p>
          <h2 className="mt-1 font-display text-xl font-bold text-ink-800 sm:text-2xl">From tradition to innovation</h2>
          <div className="mt-3 max-w-3xl space-y-3 text-[13px] leading-6 text-ink-500 sm:text-[14px] sm:leading-7">
            <p>
              Karanki Matrimony was founded in 2020 with a simple but meaningful purpose — to provide the Telugu Goud community with a dedicated, safe and modern platform to find life partners.
            </p>
            <p>
              We understand the importance of cultural compatibility, family values and trust in matrimonial alliances. That is why every profile on our platform goes through a manual verification process before it becomes visible to other members.
            </p>
            <p>
              We are a growing platform, committed to maintaining the highest standards of authenticity, privacy and community respect. Our journey has just begun.
            </p>
          </div>
        </motion.section>

        {/* Values */}
        <section className="border-b border-sand-100 py-7 sm:py-8">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-700">What we stand for</p>
            <h2 className="mt-1 font-display text-xl font-bold text-ink-800 sm:text-2xl">Our core values</h2>
          </div>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="mt-4 divide-y divide-sand-100"
          >
            {values.map(({ Icon, title, desc }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                className="flex items-start gap-3 py-4 first:pt-0 last:pb-0"
              >
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <Icon size={16} />
                </div>
                <div>
                  <h3 className="text-[13px] font-semibold text-ink-800">{title}</h3>
                  <p className="mt-1 text-[12px] leading-5 text-ink-500 sm:text-[13px] sm:leading-6">{desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* CTA */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="py-7 text-center sm:py-9"
        >
          <Star className="mx-auto mb-2 text-brand-700" size={22} />
          <h2 className="font-display text-xl font-bold text-ink-800 sm:text-2xl">
            Be part of our community
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-[13px] leading-6 text-ink-500 sm:text-[14px] sm:leading-7">
            Join Karanki Matrimony and take the first step towards finding your perfect life partner within the Goud community.
          </p>
          <Link
            to="/register"
            className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-xl bg-brand-700 px-5 text-[12px] font-semibold text-white transition hover:bg-brand-800"
          >
            Register free <ChevronRight size={16} />
          </Link>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
